import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";

/* Shared primitives for the admin console.
   Tokens only — no raw hex, no arbitrary sizes. See frontend/DESIGN_SYSTEM.md. */

export function StatCard({ label, value, hint, tone = "default", testid }) {
  const toneInk = {
    default: "text-ink",
    good: "text-state-success",
    warn: "text-state-warning-ink",
    bad: "text-ink-accent",
  }[tone] || "text-ink";
  return (
    <div className="brutal-card p-5" data-testid={testid}>
      <div className="text-2xs uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-display font-black text-3xl mt-2 tabular-nums ${toneInk}`}>{value}</div>
      {hint && <div className="text-xs text-ink-subtle mt-1">{hint}</div>}
    </div>
  );
}

export function Section({ title, children, actions, id }) {
  const headingId = id ? `${id}-heading` : undefined;
  return (
    <section className="mb-10" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 id={headingId} className="marquee-tag">{title}</h2>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** A table that stays inside its scroller.
 *  `relative` is required: .sr-only is position:absolute, and without a
 *  containing block here it escapes overflow-x-auto and widens the document. */
export function DataTable({ caption, columns, rows, empty = "Nothing here yet.", renderRow, testid }) {
  return (
    <div className="brutal-card p-0 relative overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="text-left text-2xs uppercase tracking-widest text-ink-muted">
            {columns.map((c) => (
              <th key={c.key} scope="col" className="p-3 whitespace-nowrap">
                {c.srOnly ? <span className="sr-only">{c.label}</span> : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) && (
            <tr><td colSpan={columns.length} className="p-6 text-center text-ink-muted">{empty}</td></tr>
          )}
          {rows?.map(renderRow)}
        </tbody>
      </table>
    </div>
  );
}

export function Pill({ tone = "default", children }) {
  const cls = {
    default: "brutal-chip",
    success: "brutal-chip brutal-chip-success",
    warning: "brutal-chip brutal-chip-warning",
    danger: "brutal-chip brutal-chip-danger",
    accent: "brutal-chip brutal-chip-accent",
  }[tone] || "brutal-chip";
  return <span className={cls}>{children}</span>;
}

export function Money({ amount, className = "" }) {
  const n = Number(amount || 0);
  return (
    <span className={`tabular-nums ${className}`}>
      ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
}

export function LoadingBlock({ label = "Loading…" }) {
  return (
    <div className="brutal-card p-6">
      <p className="sr-only" role="status">{label}</p>
      <div className="space-y-3" aria-hidden="true">
        <div className="brutal-skeleton h-4 w-1/3" />
        <div className="brutal-skeleton h-4 w-2/3" />
        <div className="brutal-skeleton h-4 w-1/2" />
      </div>
    </div>
  );
}

/**
 * A real WAI-ARIA tablist: roving tabIndex, arrow/Home/End keys, aria-selected,
 * and aria-controls wired to the panel. The visual state is not carried by
 * colour alone -- the selected tab is also the only one in the tab order.
 */
export function Tabs({ tabs, active, onChange, idPrefix = "admin" }) {
  const ref = useRef(null);
  const onKeyDown = (e) => {
    const i = tabs.findIndex((t) => t.id === active);
    let next = null;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    onChange(tabs[next].id);
    ref.current?.querySelector(`#${idPrefix}-tab-${tabs[next].id}`)?.focus();
  };
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label="Admin sections"
      onKeyDown={onKeyDown}
      className="flex flex-wrap brutal-border border-b-0 mb-6"
    >
      {tabs.map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            id={`${idPrefix}-tab-${t.id}`}
            role="tab"
            type="button"
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={`px-4 py-3 text-2xs uppercase tracking-widest border-r brutal-border border-t-0 border-b-0 border-l-0 ${
              selected ? "bg-surface-inverse text-ink-inverse on-ink" : "bg-canvas hover:bg-surface"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            {t.label}
            {typeof t.badge === "number" && t.badge > 0 && (
              <span className="ml-2 tabular-nums">({t.badge})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, active, idPrefix = "admin", children }) {
  if (id !== active) return null;
  return (
    <div role="tabpanel" id={`${idPrefix}-panel-${id}`} aria-labelledby={`${idPrefix}-tab-${id}`} tabIndex={0}>
      {children}
    </div>
  );
}

/** Fetch-on-mount with explicit loading and error states — no silent catch. */
export function useAdminResource(path, { params, deps = [], enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: enabled, error: "" });
  const [nonce, setNonce] = useState(0);
  const paramKey = JSON.stringify(params || {});
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    api.get(path, { params })
      .then((r) => alive && setState({ data: r.data, loading: false, error: "" }))
      .catch((e) => alive && setState({
        data: null, loading: false,
        error: e?.response?.data?.detail || `Could not load ${path}`,
      }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, paramKey, nonce, enabled, ...deps]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}
